import React, { useRef } from 'react'
import styled from 'styled-components'

import { type ClassNameProps } from '@root/shared/react'

import { type DataTableModel } from '../atoms'

import { DataTableContentSimple } from './data-table-content-simple'
import { DataTableContentVirtuoso } from './data-table-content-virtuoso'

import type { DataTableEmptyPublicProps } from './data-table-empty'
import type { Column } from './data-table-types'
import type { TableVirtuosoHandle } from 'react-virtuoso'

export type DataTableVirtualization = 'simple' | 'virtuoso'

export type DataTableContentProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  columns: ReadonlyArray<Column<D, ColumnId, Context>>
  model: DataTableModel<D, ColumnId, object, Context>
  rowHeight?: number
  renderBackgroundLink?: (data: {
    data: D
    index: number
  }) => React.ReactNode | undefined
  renderRow?: (args: {
    data: D
    element: React.ReactElement
  }) => React.ReactNode
  getRowStyle?: (row: D) => React.CSSProperties
  disableScroll?: boolean
  onScroll?: (event: React.UIEvent<HTMLDivElement, UIEvent>) => void
  onRowClick?: (value: D, e: React.MouseEvent<HTMLDivElement>) => void
  context: Context
  emptyTitle?: string
  withBackToTop?: boolean
  floatingActions?: React.ReactNode
  renderError?: ((e: unknown) => React.ReactNode) | undefined
  renderEmptyMessage?: () => React.ReactNode
  stickyHeaderTop?: number
  minWidth?: number
  virtuosoRef?: React.RefObject<TableVirtuosoHandle | null>
  scrollContainerRef?: React.RefObject<HTMLElement | null>
  virtualization?: DataTableVirtualization
  /**
   * Overscan (px above/below the viewport) that Virtuoso keeps rendered.
   * Prevents the empty container background from flashing through during fast
   * back-and-forth scrolling. Only applies to the `virtuoso` virtualization.
   */
  increaseViewportBy?: number
  customScrollParent?: HTMLElement
} & ClassNameProps &
  DataTableEmptyPublicProps

export const DataTableContent = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  virtualization = 'virtuoso',
  minWidth,
  stickyHeaderTop,
  virtuosoRef,
  scrollContainerRef,
  disableScroll,
  ...contentProps
}: DataTableContentProps<D, ColumnId, Context>) => {
  const tableAnchorRef = useRef<HTMLDivElement>(null)

  if (virtualization === 'virtuoso') {
    return (
      <ScrollContainer>
        <TableContainer ref={tableAnchorRef} style={{ minWidth }}>
          <DataTableContentVirtuoso<D, ColumnId, Context>
            {...contentProps}
            disableScroll={disableScroll}
            stickyHeaderTop={stickyHeaderTop}
            tableAnchorRef={tableAnchorRef}
            virtuosoRef={virtuosoRef}
          />
        </TableContainer>
      </ScrollContainer>
    )
  }

  return (
    <DataTableContentSimple<D, ColumnId, Context>
      {...contentProps}
      stickyHeaderTop={stickyHeaderTop}
      minWidth={minWidth}
      scrollContainerRef={scrollContainerRef}
    />
  )
}

const ScrollContainer = styled.div`
  overflow-x: auto;
  min-width: 0;
  overflow-y: hidden;
  min-height: 0;
  flex: 1;
`

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: 0;
  height: 100%;
`
